import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { useLang } from "@/landing/LanguageContext";
import { images } from "@/landing/data";
import { Reveal, Overline, CTAButton } from "@/landing/ui";

const VideoModal = ({ person, onClose }) => (
  <motion.div
    data-testid="testimonial-video-modal"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
    className="fixed inset-0 z-[70] flex items-center justify-center bg-carbon/90 p-4 backdrop-blur-sm"
  >
    <motion.div
      initial={{ scale: 0.94, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.94, opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-4xl"
    >
      <button
        data-testid="testimonial-modal-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full border border-cream/40 text-cream transition-colors hover:border-sand hover:text-sand"
      >
        <X size={18} />
      </button>
      <div className="relative aspect-video w-full overflow-hidden bg-black">
        <iframe
          src={`https://player.vimeo.com/video/${person.videoId}?autoplay=1&title=0&byline=0&portrait=0`}
          className="h-full w-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={person.name}
        />
      </div>
      <p className="mt-4 text-center font-sans text-sm tracking-[0.15em] uppercase text-cream/80">
        {person.name} · {person.role}
      </p>
    </motion.div>
  </motion.div>
);

export const TestimonialsSection = () => {
  const { t } = useLang();
  const ts = t.testimonials;
  const [active, setActive] = useState(null);
  return (
    <section id="testimonios" className="scroll-mt-20 bg-cream py-24 text-carbon lg:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="mb-14 max-w-2xl">
          <Overline>{ts.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:text-4xl lg:text-6xl">
            {ts.title}
          </h2>
          <p className="mt-6 font-sans text-base font-light leading-relaxed text-earth">
            {ts.subtitle}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ts.people.map((person, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <button
                data-testid={`testimonial-${i}`}
                onClick={() => person.videoId && setActive(person)}
                className="group block w-full text-left"
              >
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-carbon">
                  <img
                    src={person.thumb || images.testimonials[i]}
                    alt={person.name}
                    className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-carbon/70 via-transparent to-transparent" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-cream" />
                  <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-cream/60 bg-carbon/30 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:border-sand">
                    <Play size={18} className="ml-0.5 fill-cream text-cream" />
                  </span>
                </div>
                <div className="mt-4 flex items-baseline justify-between border-b border-earth/15 pb-4">
                  <span className="font-serif text-lg text-carbon">{person.name}</span>
                  <span className="font-sans text-xs tracking-[0.15em] uppercase text-earth/70">
                    {person.role}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16 flex justify-center">
          <CTAButton testid="testimonials-cta" variant="solid">
            {t.ctas.discover}
          </CTAButton>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && <VideoModal person={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};

export const AboutSection = () => {
  const { t } = useLang();
  const a = t.about;
  return (
    <section id="susana" className="scroll-mt-20 bg-beige py-24 text-carbon lg:py-32" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-12 lg:gap-20 lg:px-10">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="relative">
              <div className="relative aspect-[4/5] w-full max-w-xs overflow-hidden lg:max-w-sm">
                <img src={images.about} alt={a.name} className="h-full w-full object-cover" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-b from-transparent to-beige" />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden h-40 w-40 border border-earth/30 lg:block" />
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 border-y border-earth/20 py-6">
              {a.stats.map((s) => (
                <div key={s.l}>
                  <p className="font-serif text-3xl font-light text-carbon lg:text-4xl">{s.n}</p>
                  <p className="mt-1 font-sans text-xs leading-tight tracking-wide text-earth/80">{s.l}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 font-sans text-xs tracking-[0.25em] uppercase text-earth/70">
              {a.creatorLabel}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {a.creator.map((c) => (
                <span key={c} className="rounded-full bg-carbon px-4 py-1.5 font-sans text-xs tracking-wide text-cream">
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-6 font-sans text-xs tracking-[0.25em] uppercase text-earth/70">
              {a.formationsLabel}
            </p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
              {a.formations.map((f) => (
                <span key={f} className="font-sans text-sm font-light text-carbon/80">{f}</span>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal>
            <Overline>{a.overline}</Overline>
            <p className="mt-6 font-serif text-3xl font-light leading-tight text-balance text-carbon lg:text-4xl">
              "{a.quote}"
            </p>
            <p className="mt-6 font-sans text-sm tracking-[0.15em] uppercase text-earth">
              {a.name} · {a.role}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 space-y-5">
              {a.bio1.map((p, i) => (
                <p key={i} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                  {p}
                </p>
              ))}
            </div>
            <p className="mt-8 border-l-2 border-earth/50 pl-6 font-serif text-xl font-light italic leading-snug text-carbon lg:text-2xl">
              {a.midQuote}
            </p>
            <div className="mt-8 space-y-5">
              {a.bio2.map((p, i) => (
                <p key={i} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-8 space-y-5 border-t border-earth/20 pt-8">
              {a.methods.map((m) => (
                <p key={m.name} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                  <span className="font-medium tracking-wide text-carbon">{m.name}</span>, {m.desc}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <div className="mt-8 space-y-5">
              {a.bio3.map((p, i) => (
                <p key={i} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                  {p}
                </p>
              ))}
            </div>
            <blockquote className="mt-8 border-l-2 border-earth/50 pl-6 font-serif text-xl font-light italic leading-snug text-carbon lg:text-2xl">
              {a.mission}
            </blockquote>
            <p className="mt-8 font-sans text-base font-light leading-relaxed text-carbon lg:text-lg">
              {a.missionClose}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};


export const FacultySection = () => {
  const { t } = useLang();
  const f = t.faculty;
  return (
    <section className="bg-beige py-20 text-carbon lg:py-28" data-testid="faculty-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <Reveal className="mb-12 max-w-2xl">
          <Overline>{f.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:text-4xl lg:text-5xl">
            {f.title}
          </h2>
          <p className="mt-5 font-sans text-base font-light leading-relaxed text-earth">
            {f.subtitle}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          {f.people.map((person, i) => (
            <Reveal key={i} delay={(i % 4) * 0.07} className="text-center">
              <div className="mx-auto h-36 w-36 overflow-hidden rounded-full border border-earth/20 lg:h-40 lg:w-40" data-testid={`faculty-photo-${i}`}>
                <img src={person.img} alt={person.name} className="h-full w-full object-cover" />
              </div>
              <h3 className="mt-6 font-serif text-xl font-light text-carbon">{person.name}</h3>
              <p className="mx-auto mt-3 max-w-xs font-sans text-sm font-light leading-relaxed text-earth">
                {person.role}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
